import { useEffect } from 'react';

import { useSearchParams } from 'react-router';
import { DEFAULT_LANGUAGE } from 'src/constants/constants';
import { setMainQuestionnaire } from 'src/store/actions/actionCreators';
import { fetchSkjemaoppgave } from 'src/store/actions/getSkjemaActionCreator';
import { useAppDispatch, useAppSelector } from 'src/store/reducers';
import { getLanguagesInDefinition } from 'src/util/getLanguagesInDefinition';
import { isLanguageInAvailableLanguages, isLanguageInFormDefinition, LanguageLocales } from 'src/util/portalLanguage-utils';

import { setAllowNavigation } from '@helsenorge/framework-utils/hn-user';
import { selectCurrentLanguage } from '@helsenorge/framework-utils/language';

export const useHandleLanguageChange = (): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const language = useAppSelector(state => selectCurrentLanguage(state.language));
  const availableLanguages = useAppSelector(state => state.refero.ui.availableLanguages);

  const questionnaires = useAppSelector(state => state.refero.ui.questionnaires);
  const metadata = useAppSelector(state => state.refero.ui.userMetadata);
  const questionnaireResponse = useAppSelector(state => state.refero.form.FormData.Content);
  const isMicroweb = useAppSelector(state => state.microweb.isMicroweb);
  const langFromUrl = searchParams.get('lang') as LanguageLocales | null;

  useEffect(() => {
    if (!isMicroweb) {
      setAllowNavigation(true);
    }
    const languagesInDefinition = availableLanguages || getLanguagesInDefinition(questionnaires);

    if (languagesInDefinition.length === 0 && langFromUrl !== null) {
      dispatch(fetchSkjemaoppgave({ language: langFromUrl }));
    } else if (languagesInDefinition.length === 0 && langFromUrl === null) {
      dispatch(fetchSkjemaoppgave({ language: undefined }));
    } else {
      const langIsAvailable = isLanguageInAvailableLanguages(language, languagesInDefinition);
      const lang = langIsAvailable ? language : DEFAULT_LANGUAGE;
      const langIsAvailableButNotDownloaded =
        !isLanguageInFormDefinition(language, getLanguagesInDefinition(questionnaires)) &&
        isLanguageInAvailableLanguages(language, languagesInDefinition);
      if (!isMicroweb) {
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set('lang', lang);
        setSearchParams(updatedParams, { replace: true });
        setAllowNavigation(false);
      }

      if (langIsAvailableButNotDownloaded) {
        dispatch(fetchSkjemaoppgave({ language: lang }));
      } else {
        if (metadata) {
          dispatch(
            setMainQuestionnaire({
              questionnaires,
              metadata,
              questionnaireResponse: questionnaireResponse ? JSON.stringify(questionnaireResponse) : undefined,
              availableLanguages: languagesInDefinition,
            })
          );
        }
      }
    }

    return (): void => {
      if (!isMicroweb) {
        setAllowNavigation(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, langFromUrl, metadata]);
};
export default useHandleLanguageChange;
